/* eslint-disable react/require-default-props */
import React from 'react';
import {ActivityIndicator, Platform, View} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useNavigation} from '@react-navigation/native';

import TouchableOpacity from '/component/base/TouchableOpacity';
import {ReportEmptyState} from '/component/partial/report/ReportEmptyState/ReportEmptyState';
import {ManageMySiteTestIDs} from '/constant/TestIDs';

import {
  BusinessProfileApiPayload,
  BusinessProfileApiResponse,
  SocialLink,
  SocialMediaApiPayload,
  SocialMediaApiResponse,
} from '../ManageMySite.types';
import {ManageMySiteEditScreen} from '../ManageMySiteEditScreen';
import {ManageMySiteViewScreen} from '../ManageMySiteViewScreen';
import {SocialLinksScreen} from '../SocialLinksScreen';

import {styles} from './ManageMyContainer.style';
import {useManageMySiteContainer} from './ManageMySiteContainer.hook';
import {UploadBusinessProfileImagePayload} from './ManageMySiteContainer.types';

type Props = {
  businessProfile?: BusinessProfileApiResponse & {socialLinks?: SocialLink[]};
  isBusinessProfileLoading?: boolean;
  isBusinessProfileSaving?: boolean;
  hasBusinessProfile?: boolean;
  isBusinessProfileError?: boolean;
  getBusinessProfile?: () => Promise<void> | void;
  patchBusinessProfile?: (
    payload: Partial<BusinessProfileApiPayload>,
  ) => Promise<void>;

  socialMedia?: SocialMediaApiResponse;
  isSocialMediaLoading?: boolean;
  isSocialMediaSaving?: boolean;
  hasSocialMedia?: boolean;
  isSocialMediaError?: boolean;
  getSocialMedia?: () => Promise<void> | void;
  patchSocialMedia?: (payload: SocialMediaApiPayload) => Promise<void>;
  uploadBusinessProfileImage?: (
    payload: UploadBusinessProfileImagePayload,
  ) => Promise<any>;
};

export const ManageMySiteContainerComponent = ({
  businessProfile,
  isBusinessProfileLoading = false,
  hasBusinessProfile,
  getBusinessProfile,
  patchBusinessProfile,
  isBusinessProfileSaving = false,
  isBusinessProfileError = false,
  socialMedia,
  isSocialMediaLoading = false,
  isSocialMediaSaving = false,
  hasSocialMedia,
  isSocialMediaError = false,
  getSocialMedia,
  patchSocialMedia,
  uploadBusinessProfileImage,
}: Props) => {
  const navigation = useNavigation();

  const {
    mode,
    setMode,
    profileData,
    socialLinks,
    savingSection,
    handlePhotoUpload,
    handleSaveProfile,
    handleSaveSocialLinks,
  } = useManageMySiteContainer({
    businessProfile,
    hasBusinessProfile,
    getBusinessProfile,
    patchBusinessProfile,
    socialMedia,
    hasSocialMedia,
    getSocialMedia,
    patchSocialMedia,
    uploadBusinessProfileImage,
  });

  const handleClose = (): void => {
    if (mode === 'edit' || mode === 'social') {
      setMode('view');
      return;
    }

    navigation.goBack();
  };

  if (isBusinessProfileLoading && !hasBusinessProfile) {
    return (
      <View style={styles.loadingContainer} testID={ManageMySiteTestIDs.loader}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (isBusinessProfileError && !hasBusinessProfile && Platform.OS === 'web') {
    return null;
  }

  if (isBusinessProfileError && !hasBusinessProfile) {
    return (
      <SafeAreaView style={styles.errorScreen}>
        <TouchableOpacity
          style={styles.errorCard}
          onPress={handleClose}
          accessible
          accessibilityRole="button"
          accessibilityLabel="Unable to load profile. Please try again later."
          accessibilityHint="Double tap to go back"
          testID={ManageMySiteTestIDs.errorText}>
          <View
            accessible={false}
            importantForAccessibility="no-hide-descendants">
            <ReportEmptyState
              customTitle="Unable to load profile"
              customSubtitle="Please try again later."
            />
          </View>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  if (mode === 'social') {
    return (
      <SocialLinksScreen
        socialLinks={socialLinks}
        isSaving={isSocialMediaSaving}
        onClose={() => setMode('view')}
        onSave={handleSaveSocialLinks}
      />
    );
  }

  if (mode === 'view') {
    return (
      <ManageMySiteViewScreen
        {...profileData}
        socialLinks={socialLinks}
        hasBusinessProfile={hasBusinessProfile}
        hasSocialMedia={hasSocialMedia}
        isBusinessProfileLoading={isBusinessProfileLoading}
        isSocialMediaLoading={isSocialMediaLoading}
        savingSection={savingSection}
        onClose={handleClose}
        onEdit={() => setMode('edit')}
        onEditSocialLinks={() => setMode('social')}
        isSocialMediaError={isSocialMediaError}
        getSocialMedia={getSocialMedia}
        getBusinessProfile={getBusinessProfile}
      />
    );
  }

  return (
    <ManageMySiteEditScreen
      {...profileData}
      isSaving={isBusinessProfileSaving}
      onClose={handleClose}
      onSave={handleSaveProfile}
      onPhotoUpload={handlePhotoUpload}
    />
  );
};

export const ManageMySiteContainer = React.memo(ManageMySiteContainerComponent);
